import React, { memo, useMemo } from 'react'

import { MoreVertOutlined } from '@mui/icons-material'
import { Box, Checkbox, Divider, IconButton, Tooltip, Typography } from '@mui/material'

import { GetContentsByUserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import SessionRequireQualificationsStatusIcon from '@tabeeb/modules/sessionsPage/components/StatusIcons/SessionRequireQualificationsStatusIcon'
import SessionParticipantsCountBadge from './SessionParticipantsCountBadge'
import SessionPagesCountBadge from './SessionPagesCountBadge'
import UnreadMessagesBubble from '../../UnreadMessagesBubble'
import { CallActiveIcon, SessionLockedStatusIcon, SessionPublishedStatusIcon } from '../../StatusIcons'

import { Container, PreviewFooter, Footer, Preview } from './styles'

const defaultThumbnailUrl = new URL(
  '@tabeeb/assets/images/session-thumbnail-placeholder.jpg',
  import.meta.url
).toString()

interface SessionTileProps {
  draggable?: boolean
  selected?: boolean
  selectable?: boolean
  onClick: (session: GetContentsByUserResponse) => void
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, session: GetContentsByUserResponse) => void
  onMenu?: (e: React.MouseEvent, session: GetContentsByUserResponse) => void
  onSelect?: (selected: boolean, session: GetContentsByUserResponse) => void
  session: GetContentsByUserResponse
}

const SessionTile: React.FC<SessionTileProps> = ({
  session,
  draggable = false,
  selectable,
  selected = false,
  onDragStart,
  onClick,
  onMenu,
  onSelect,
}) => {
  const missingQualifications = session.MissingQualifications || []
  const active = session.IsActive && missingQualifications.length === 0
  const thumbnail = session.ThumbnailUrl || defaultThumbnailUrl
  const isDefaultThumbnail = !session.ThumbnailUrl

  const onOpenMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (onMenu) {
      onMenu(e, session)
    }
  }

  const onSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (onSelect) {
      onSelect(!selected, session)
    }
  }

  const lockedSessionIcon = useMemo(() => {
    if (missingQualifications.length > 0) {
      return <SessionRequireQualificationsStatusIcon />
    }
    return <SessionLockedStatusIcon />
  }, [missingQualifications.length])

  return (
    <Container
      variant='outlined'
      data-selected={selected}
      draggable={draggable}
      onClick={() => onClick(session)}
      onDragStart={(e) => {
        if (onDragStart) {
          onDragStart(e, session)
        }
      }}
    >
      {session.HasUnreadMessages && <UnreadMessagesBubble />}
      {selectable && (
        <Tooltip title={selected ? 'Unselect' : 'Select'}>
          <Checkbox
            sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
            color='primary'
            checked={selected}
            onClick={onSelectClick}
          />
        </Tooltip>
      )}
      <Preview data-active={active} data-default-preview={isDefaultThumbnail} image={thumbnail}>
        <PreviewFooter>
          {!!session.Slides && <SessionPagesCountBadge pages={session.Slides} />}
          {!!session.UsersCount && <SessionParticipantsCountBadge participants={session.UsersCount} />}
        </PreviewFooter>
      </Preview>
      <Divider />
      <Footer>
        <Box display='flex' alignItems='center' flexGrow={1} minWidth={0}>
          <Box minWidth={0}>
            <Box display='flex' alignItems='center'>
              {!active && lockedSessionIcon}
              {session.IsPublished && <SessionPublishedStatusIcon />}
              {session.IsCallActive && <CallActiveIcon />}
              <Typography variant='body2' title={session.Description} fontWeight={600} noWrap>
                {session.Description}
              </Typography>
            </Box>
            {session.Owner && (
              <Typography variant='body2' title={session.Owner.Name} noWrap>
                {session.Owner.Name}
              </Typography>
            )}
          </Box>
        </Box>

        {onMenu && (
          <IconButton size='small' onClick={onOpenMenuClick} color='inherit'>
            <MoreVertOutlined />
          </IconButton>
        )}
      </Footer>
    </Container>
  )
}

export default memo(SessionTile)
